import React, { useEffect, useState, useCallback } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '../../http';
import Modal, { ModalButton } from '../ui/Modal';

const ROLE_LABELS = {
  owner: 'Владелец',
  manager: 'Менеджер',
  viewer: 'Просмотр',
};

export default function UserClientsManager({ userId }) {
  const [items, setItems] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newClientId, setNewClientId] = useState('');
  const [newRole, setNewRole] = useState('manager');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true); setError('');
    try {
      const [{ data: list }, { data: clients }] = await Promise.all([
        api.get(`v1/users/${userId}/clients`),
        api.get('v1/clients'),
      ]);
      setItems(Array.isArray(list) ? list : []);
      setAllClients(Array.isArray(clients) ? clients.filter((c) => !c.is_deleted) : []);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const available = allClients.filter(
    (c) => !items.some((it) => String(it.id_clients) === String(c.id))
  );

  const handleAttach = async () => {
    if (!newClientId) return;
    setSubmitting(true); setError('');
    try {
      await api.post(`v1/users/${userId}/clients`, {
        id_clients: Number(newClientId),
        role_in_client: newRole,
      });
      setDialogOpen(false);
      setNewClientId('');
      setNewRole('manager');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Не удалось привязать');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDetach = async (clientId) => {
    setError('');
    try {
      await api.delete(`v1/users/${userId}/clients/${clientId}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Не удалось отвязать');
    }
  };

  return (
    <section className="adminform__card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div className="adminform__section-title" style={{ margin: 0 }}>Доступ к клиентам</div>
        <button
          type="button"
          className="adminbtn adminbtn--primary"
          onClick={() => setDialogOpen(true)}
          disabled={!available.length}
          style={{ height: 32, padding: '0 12px', fontSize: 12.5 }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          Привязать клиента
        </button>
      </div>

      {!!error && <div className="crmpage__alert crmpage__alert--error" style={{ marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <div className="adminform__hint">Загрузка…</div>
      ) : items.length === 0 ? (
        <div className="adminform__hint">Пользователь не привязан ни к одному клиенту.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((it) => (
            <div
              key={it.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                border: '1px solid var(--border)',
                borderRadius: 8,
                background: '#fff',
              }}
            >
              <span style={{ flex: 1, fontWeight: 500, color: 'var(--fg)' }}>{it.name}</span>
              <span className="crmpage__chip crmpage__chip--warning" style={{ marginTop: 0 }}>
                {ROLE_LABELS[it.role_in_client] || it.role_in_client}
              </span>
              {!it.is_active && (
                <span className="crmpage__chip" style={{ marginTop: 0 }}>Неактивна</span>
              )}
              <button
                type="button"
                className="adminbtn adminbtn--icon"
                title="Отвязать"
                onClick={() => handleDetach(it.id_clients)}
              ><DeleteOutlineIcon sx={{ fontSize: 18 }} /></button>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Привязать клиента"
        size="sm"
        footer={
          <>
            <ModalButton variant="secondary" onClick={() => setDialogOpen(false)}>Отмена</ModalButton>
            <ModalButton variant="primary" onClick={handleAttach} disabled={!newClientId || submitting}>
              {submitting ? 'Сохранение…' : 'Привязать'}
            </ModalButton>
          </>
        }
      >
        <div className="adminform__grid adminform__grid--full">
          <label className="adminform__field">
            <span className="adminform__label">Клиент</span>
            <select className="adminform__select" value={newClientId} onChange={(e) => setNewClientId(e.target.value)}>
              <option value="">— выберите —</option>
              {available.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {!available.length && <span className="adminform__hint">Все клиенты уже привязаны.</span>}
          </label>
          <label className="adminform__field">
            <span className="adminform__label">Роль в клиенте</span>
            <select className="adminform__select" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
              <option value="owner">Владелец</option>
              <option value="manager">Менеджер</option>
              <option value="viewer">Просмотр</option>
            </select>
          </label>
        </div>
      </Modal>
    </section>
  );
}
